import ReactGA from "react-ga4";
import ReactPixel from "react-facebook-pixel";

let _ga4Initialized = false;
let _fbPixelInitialized = false;
let _currentMeasurementId = null;
let _currentPixelId = null;
let _debug = false;

// Session context - persisted throughout the booking flow
let _sessionContext = {
  sessionId: null,
  sessionStartTime: null,
  entryUrl: null,
  referrer: null,
  patientType: null, // 'new' or 'existing'
  patientId: null,
  locationId: null,
  locationName: null,
  serviceId: null,
  serviceName: null,
  servicePrice: null,
  practitionerId: null,
  practitionerName: null,
  currentStep: null,
  stepStartTime: null,
  isStandaloneMode: false,
};

// Step timing tracking
const _stepTimings = {};

/**
 * Generate a unique session ID
 */
function generateSessionId() {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Initialize GA4 and Facebook Pixel.
 * Safe to call multiple times — will only initialize once per ID.
 */
export function initialize({ measurementId, pixelId, debug = false }) {
  _debug = debug;

  if (measurementId && measurementId !== _currentMeasurementId) {
    ReactGA.initialize(measurementId, {
      testMode: debug,
    });
    _currentMeasurementId = measurementId;
    _ga4Initialized = true;

    if (_debug) {
      console.log("[Analytics] GA4 initialized:", measurementId);
    }
  }

  if (pixelId && pixelId !== _currentPixelId) {
    ReactPixel.init(pixelId, {}, { autoConfig: true, debug });
    _currentPixelId = pixelId;
    _fbPixelInitialized = true;

    if (_debug) {
      console.log("[Analytics] Facebook Pixel initialized:", pixelId);
    }
  }

  // Initialize session on first init
  if (!_sessionContext.sessionId) {
    startSession();
  }
}

/**
 * Start a new analytics session - tracks entry point and referrer
 */
export function startSession() {
  _sessionContext.sessionId = generateSessionId();
  _sessionContext.sessionStartTime = Date.now();
  _sessionContext.entryUrl = window.location.href;
  _sessionContext.referrer = document.referrer || 'direct';

  trackEvent('session_started', {
    entry_url: _sessionContext.entryUrl,
    referrer: _sessionContext.referrer,
    is_embedded: window.self !== window.top,
  });

  if (_debug) {
    console.log("[Analytics] Session started:", _sessionContext);
  }
}

/**
 * Set session context values that persist across events
 */
export function setSessionContext(context) {
  Object.assign(_sessionContext, context);

  if (_debug) {
    console.log("[Analytics] Session context updated:", _sessionContext);
  }
}

/**
 * Get current session context
 */
export function getSessionContext() {
  return { ..._sessionContext };
}

/**
 * Track step entry - records time for step duration tracking
 */
export function trackStepEntry(stepNumber, stepName) {
  const now = Date.now();

  // Record time spent on previous step if exists
  if (_sessionContext.currentStep && _sessionContext.stepStartTime) {
    const previousStepDuration = now - _sessionContext.stepStartTime;
    _stepTimings[_sessionContext.currentStep] = previousStepDuration;

    trackEvent('step_time_spent', {
      step_number: _sessionContext.currentStep,
      duration_ms: previousStepDuration,
      duration_seconds: Math.round(previousStepDuration / 1000),
    });
  }

  _sessionContext.currentStep = stepNumber;
  _sessionContext.stepStartTime = now;
}

/**
 * Track a page view with step information on both GA4 and Facebook Pixel.
 */
export function trackPageView({
  pageTitle,
  pageName,
  pagePath,
  pageUrl,
  stepNumber,
  stepName,
  stepPercentage
}) {
  const params = {
    page: pagePath,
    title: pageTitle,
    step_number: stepNumber,
    step_name: stepName,
    step_percentage: stepPercentage,
    patient_type: _sessionContext.patientType,
    location_id: _sessionContext.locationId,
    service_id: _sessionContext.serviceId,
    service_name: _sessionContext.serviceName,
    is_standalone: _sessionContext.isStandaloneMode,
  };

  // Track step entry for timing
  if (stepNumber) {
    trackStepEntry(stepNumber, stepName);
  }

  if (_ga4Initialized) {
    ReactGA.send({
      hitType: "pageview",
      ...params,
    });
  }

  if (_fbPixelInitialized) {
    ReactPixel.pageView();
    // Also track as custom event with step info for FB
    ReactPixel.trackCustom('StepViewed', {
      step_number: stepNumber,
      step_name: stepName,
      patient_type: _sessionContext.patientType,
    });
  }

  if (_debug) {
    console.log("[Analytics] PageView:", params);
  }
}

/**
 * Track a custom event on GA4 only.
 * Automatically includes session context.
 */
export function trackEvent(eventName, params = {}) {
  const enrichedParams = {
    ...params,
    session_id: _sessionContext.sessionId,
    patient_type: _sessionContext.patientType,
    patient_id: _sessionContext.patientId,
    location_id: _sessionContext.locationId,
    service_id: _sessionContext.serviceId,
    service_name: _sessionContext.serviceName,
    current_step: _sessionContext.currentStep,
  };

  // Remove null/undefined values
  Object.keys(enrichedParams).forEach(key => {
    if (enrichedParams[key] === null || enrichedParams[key] === undefined) {
      delete enrichedParams[key];
    }
  });

  if (_ga4Initialized) {
    ReactGA.event(eventName, enrichedParams);
  }

  if (_debug) {
    console.log("[Analytics] Event:", eventName, enrichedParams);
  }
}

/**
 * Track a Facebook Pixel standard event (e.g. Lead, Schedule, Purchase).
 * Automatically includes session context.
 */
export function trackFBStandardEvent(eventName, params = {}) {
  const enrichedParams = {
    ...params,
    patient_type: _sessionContext.patientType,
    service_name: _sessionContext.serviceName,
  };

  if (_fbPixelInitialized) {
    ReactPixel.track(eventName, enrichedParams);
  }

  if (_debug) {
    console.log("[Analytics] FB Standard Event:", eventName, enrichedParams);
  }
}

/**
 * Track a Facebook Pixel custom event.
 */
export function trackFBCustomEvent(eventName, params = {}) {
  const enrichedParams = {
    ...params,
    patient_type: _sessionContext.patientType,
    service_name: _sessionContext.serviceName,
    current_step: _sessionContext.currentStep,
  };

  if (_fbPixelInitialized) {
    ReactPixel.trackCustom(eventName, enrichedParams);
  }

  if (_debug) {
    console.log("[Analytics] FB Custom Event:", eventName, enrichedParams);
  }
}

/**
 * Track button click - for comprehensive click tracking
 */
export function trackButtonClick(buttonName, additionalParams = {}) {
  trackEvent('button_clicked', {
    button_name: buttonName,
    page_title: document.title,
    ...additionalParams,
  });

  trackFBCustomEvent('ButtonClicked', {
    button_name: buttonName,
    ...additionalParams,
  });
}

/**
 * Track funnel drop-off - when user leaves without completing
 */
export function trackFunnelDropoff(reason = 'unknown') {
  const sessionDuration = _sessionContext.sessionStartTime
    ? Date.now() - _sessionContext.sessionStartTime
    : 0;

  trackEvent('funnel_dropoff', {
    dropoff_step: _sessionContext.currentStep,
    dropoff_reason: reason,
    session_duration_ms: sessionDuration,
    session_duration_seconds: Math.round(sessionDuration / 1000),
    completed_steps: Object.keys(_stepTimings),
  });

  trackFBCustomEvent('FunnelDropoff', {
    dropoff_step: _sessionContext.currentStep,
    patient_type: _sessionContext.patientType,
  });

  if (_debug) {
    console.log("[Analytics] Funnel dropoff:", {
      step: _sessionContext.currentStep,
      reason,
      duration: sessionDuration,
    });
  }
}

/**
 * Track form errors
 */
export function trackFormError(formName, errorType, errorMessage) {
  trackEvent('form_error', {
    form_name: formName,
    error_type: errorType,
    error_message: errorMessage,
  });
}

/**
 * Track date picker interaction
 */
export function trackDatePickerInteraction(interactionType, params = {}) {
  trackEvent('date_picker_interaction', {
    interaction_type: interactionType, // 'scroll', 'month_change', 'date_tap'
    ...params,
  });
}

/**
 * Check initialization status.
 */
export function isInitialized() {
  return { ga4: _ga4Initialized, fbPixel: _fbPixelInitialized };
}

/**
 * Reset session context (e.g., after booking complete or "Book another")
 */
export function resetSession() {
  const previousSessionId = _sessionContext.sessionId;

  _sessionContext = {
    sessionId: generateSessionId(),
    sessionStartTime: Date.now(),
    entryUrl: window.location.href,
    referrer: previousSessionId ? 'book_another' : document.referrer,
    patientType: null,
    patientId: null,
    locationId: _sessionContext.locationId, // Keep location
    locationName: _sessionContext.locationName,
    serviceId: null,
    serviceName: null,
    servicePrice: null,
    practitionerId: null,
    practitionerName: null,
    currentStep: null,
    stepStartTime: null,
    isStandaloneMode: _sessionContext.isStandaloneMode,
  };

  Object.keys(_stepTimings).forEach(key => delete _stepTimings[key]);

  if (_debug) {
    console.log("[Analytics] Session reset:", _sessionContext);
  }
}
